// Everything the coach's Readiness Rate report shows for one client over a period, built from the
// daily stats the page already holds and the client's scheduled training days. Pure: no React, no
// network, no dates read from the clock (`todayUtc` is a parameter).
//
// Each questionnaire is judged against the client's own normal of the 6 weeks before it (see
// baseline.js). Output is data only: texts and colors belong to the view, which turns each
// insight's `type` + `params` into a translated sentence.

import {
  buildBaselineAt,
  getReadinessTotal,
  toReadinessEntries,
} from "./baseline";
import { INCLUDE_CURRENT_IN_NORMAL, READINESS_KEYS } from "./constants";
import { dayNumToUtc, utcToDayNum } from "./dates";
import { buildInsights, lowestEverMonths, missedDaysInsight } from "./insights";
import { round1, roundWhole } from "./math";
import { getAnswerFlag, getReadinessStatus } from "./status";
import { buildQuestions, buildSummary } from "./summary";

const positiveNumber = (value) =>
  Number.isFinite(value) && value > 0 ? value : null;

// The extras a day can carry besides the questionnaire. The last stat of a day wins.
const indexStats = (dailyStats) => {
  const byDay = new Map();
  for (const stat of dailyStats ?? []) {
    if (stat?.utcDate == null) continue;
    byDay.set(utcToDayNum(stat.utcDate), {
      sessionRpe: positiveNumber(round1(stat.sessionRPE)),
      hrv: positiveNumber(roundWhole(stat.hrv)),
      hrvSource: stat.hrvSource ?? null,
      restingHr: positiveNumber(roundWhole(stat.restingHeartRate)),
    });
  }
  return byDay;
};

const buildQuestionnaireDay = ({ entry, baseline, extras, isTrainingDay }) => {
  const total = getReadinessTotal(entry.readiness);
  const answers = READINESS_KEYS.map((key) => {
    const range = baseline.ready ? baseline[key] : null;
    return {
      key,
      value: entry.readiness[key],
      lo: range?.lo ?? null,
      hi: range?.hi ?? null,
      flag: getAnswerFlag(entry.readiness[key], range),
    };
  });
  return {
    kind: "questionnaire",
    day: entry.day,
    utcDate: entry.utcDate,
    total,
    status: getReadinessStatus(total, baseline),
    normal: baseline.ready ? baseline.total : null,
    answers,
    isTrainingDay,
    sessionRpe: extras?.sessionRpe ?? null,
    hrv: extras?.hrv ?? null,
    hrvSource: extras?.hrvSource ?? null,
    restingHr: extras?.restingHr ?? null,
  };
};

// The scheduled training days that count: past days, and today only once its questionnaire is
// done (an unfinished today is not "missed" yet).
const countedTrainingDays = ({
  trainingSet,
  fromDay,
  toDay,
  todayDay,
  entries,
}) => {
  const counted = [];
  for (const day of trainingSet) {
    if (day < fromDay || day > toDay) continue;
    if (day < todayDay || entries.has(day)) counted.push(day);
  }
  return counted.sort((a, b) => a - b);
};

const entriesBetween = (entries, fromDay, toDay) => {
  const found = [];
  for (let day = fromDay; day <= toDay; day++) {
    const entry = entries.get(day);
    if (entry) found.push(entry);
  }
  return found;
};

/**
 * Builds the overlays for the readiness report.
 * @param {*} statsByDay
 * @param {*} fromDay
 * @param {*} toDay
 * @returns
 */
const buildOverlays = (statsByDay, fromDay, toDay) => {
  const overlays = { sessionRpe: [], hrv: [], restingHr: [], hrvSources: [] };
  const sources = new Set();
  for (let day = fromDay; day <= toDay; day++) {
    const extras = statsByDay.get(day);
    if (!extras) continue;
    const utcDate = dayNumToUtc(day);
    if (extras.sessionRpe != null) {
      overlays.sessionRpe.push({ utcDate, value: extras.sessionRpe });
    }
    if (extras.hrv != null) {
      overlays.hrv.push({
        utcDate,
        value: extras.hrv,
        source: extras.hrvSource,
      });
      if (extras.hrvSource) sources.add(extras.hrvSource);
    }
    if (extras.restingHr != null) {
      overlays.restingHr.push({ utcDate, value: extras.restingHr });
    }
  }
  overlays.hrvSources = [...sources];
  return overlays;
};

// The total's normal of every day of the period, so the chart's band is continuous (also across
// the days without a questionnaire). Only the days that have a normal.
const buildNormalBand = ({
  entries,
  fromDay,
  toDay,
  baselines,
  includeCurrent,
}) => {
  const band = [];
  for (let day = fromDay; day <= toDay; day++) {
    const baseline =
      baselines.get(day) ?? buildBaselineAt(entries, day, includeCurrent);
    if (!baseline.ready || !baseline.total) continue;
    band.push({
      utcDate: dayNumToUtc(day),
      lo: baseline.total.lo,
      hi: baseline.total.hi,
    });
  }
  return band;
};

/**
 * Builds the empty report for the readiness report.
 * @param {*} params
 * @returns
 */
const emptyReport = ({ state, fromDay, toDay }) => ({
  state,
  period: {
    fromUtc: dayNumToUtc(fromDay),
    toUtc: dayNumToUtc(toDay),
    days: toDay - fromDay + 1,
    isLifetime: false,
  },
  normal: null,
  summary: {
    avg: null,
    prevAvg: null,
    change: null,
    questionnaires: 0,
    judged: 0,
    below: 0,
    trainingDays: 0,
    completedTrainingDays: 0,
    completionPct: null,
    missedDays: [],
    avgRpe: null,
  },
  questions: [],
  days: [],
  overlays: { sessionRpe: [], hrv: [], restingHr: [], hrvSources: [] },
  normalBand: [],
  latest: null,
  insights: [],
});

/**
 * @param {object} params
 * @param {object[]} params.dailyStats - the client's daily stats (any range; only complete
 *   questionnaires count; sessionRPE, hrv, hrvSource and restingHeartRate ride along)
 * @param {number[]} params.trainingDays - YYYYMMDD of the days with a scheduled workout
 * @param {number|null} params.fromUtc - period start; null means lifetime (from the first one)
 * @param {number|null} params.toUtc - period end; defaults to today
 * @param {number} params.todayUtc - today, in the viewer's calendar
 * @param {boolean} [params.includeCurrent] - whether a day's own questionnaire is part of its normal
 */
export const buildReadinessReport = ({
  dailyStats = [],
  trainingDays = [],
  fromUtc = null,
  toUtc = null,
  todayUtc,
  includeCurrent = INCLUDE_CURRENT_IN_NORMAL,
}) => {
  const todayDay = utcToDayNum(todayUtc);
  const toDay = Math.min(
    toUtc == null ? todayDay : utcToDayNum(toUtc),
    todayDay
  );
  const entries = toReadinessEntries(dailyStats);
  if (entries.size === 0) {
    return emptyReport({ state: "noData", fromDay: toDay, toDay });
  }

  let firstDay = Infinity;
  for (const day of entries.keys()) firstDay = Math.min(firstDay, day);
  const isLifetime = fromUtc == null;
  const fromDay = Math.min(isLifetime ? firstDay : utcToDayNum(fromUtc), toDay);
  const periodLength = toDay - fromDay + 1;

  const statsByDay = indexStats(dailyStats);
  const trainingSet = new Set((trainingDays ?? []).map(utcToDayNum));

  const periodEntries = entriesBetween(entries, fromDay, toDay);
  const baselines = new Map();
  const questionnaireDays = periodEntries.map((entry) => {
    const baseline = buildBaselineAt(entries, entry.day, includeCurrent);
    baselines.set(entry.day, baseline);
    return buildQuestionnaireDay({
      entry,
      baseline,
      extras: statsByDay.get(entry.day),
      isTrainingDay: trainingSet.has(entry.day),
    });
  });

  const counted = countedTrainingDays({
    trainingSet,
    fromDay,
    toDay,
    todayDay,
    entries,
  });
  const missed = counted.filter((day) => !entries.has(day));
  const missedDays = missed.map((day) => ({
    kind: "missed",
    day,
    utcDate: dayNumToUtc(day),
  }));

  const overlays = buildOverlays(statsByDay, fromDay, toDay);
  const base = emptyReport({ state: "empty", fromDay, toDay });
  const period = { ...base.period, isLifetime };
  if (questionnaireDays.length === 0) {
    const summary = {
      ...base.summary,
      trainingDays: counted.length,
      completedTrainingDays: 0,
      completionPct: counted.length > 0 ? 0 : null,
      missedDays: missed.map(dayNumToUtc),
    };
    const missedInsight = missedDaysInsight(summary);
    return {
      ...base,
      period,
      overlays,
      days: missedDays,
      summary,
      insights: missedInsight ? [missedInsight] : [],
    };
  }

  const latestDay = questionnaireDays.at(-1);
  const normal = baselines.get(latestDay.day);
  const previousEntries = isLifetime
    ? []
    : entriesBetween(entries, fromDay - periodLength, fromDay - 1);
  const showChange = previousEntries.length > 0 && normal.ready;
  const questions = buildQuestions({
    periodDays: questionnaireDays,
    previousEntries,
    normal,
    showChange,
  });
  const summary = buildSummary({
    questionnaireDays,
    previousEntries,
    showChange,
    counted,
    missed,
    overlays,
  });
  const latest = {
    utcDate: latestDay.utcDate,
    total: latestDay.total,
    status: latestDay.status,
    sessionRpe: latestDay.sessionRpe,
  };
  const state = normal.ready ? "typical" : "new";

  return {
    state,
    period,
    normal,
    summary,
    questions,
    days: [...questionnaireDays, ...missedDays].sort((a, b) => a.day - b.day),
    overlays,
    normalBand: buildNormalBand({
      entries,
      fromDay,
      toDay,
      baselines,
      includeCurrent,
    }),
    latest,
    insights: buildInsights({
      state,
      normal,
      summary,
      questions,
      questionnaireDays,
      latest,
      lowestMonths: lowestEverMonths({
        latestDay: latestDay.day,
        firstDay,
        entries,
        toDay,
      }),
    }),
  };
};
